import React, { useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator, ClipboardCheck, FileText, Sparkles } from "lucide-react";

import SEO, { buildFaqJsonLd } from "@/components/SEO";
import BreadcrumbNav from "@/components/BreadcrumbNav";
import FAQAccordion from "@/components/FAQAccordion";

import MaintenancePlanCard from "@/components/MaintenancePlanCard";
import MaintenanceWaitlistCard from "@/components/maintenance/MaintenanceWaitlistCard";
import PlanRecommender from "@/components/maintenance/PlanRecommender";
import SqftPricingTable from "@/components/maintenance/SqftPricingTable";
import MembershipIncludesCards from "@/components/maintenance/MembershipIncludesCards";
import MaintenanceInspectionSection from "@/components/maintenance/MaintenanceInspectionSection";
import CrewHoursAndAllowanceSection from "@/components/maintenance/CrewHoursAndAllowanceSection";
import EmergencyDispatchSection from "@/components/maintenance/EmergencyDispatchSection";
import ExclusionsAndMaterialsSection from "@/components/maintenance/ExclusionsAndMaterialsSection";
import MembershipProcessSteps from "@/components/maintenance/MembershipProcessSteps";
import MembershipRequestForm from "@/components/maintenance/MembershipRequestForm";
import MaintenanceFinalCTA from "@/components/maintenance/MaintenanceFinalCTA";

import {
  RESIDENTIAL_PLANS,
  COMMERCIAL_PLANS,
  RESIDENTIAL_PRICING_NOTE,
  FINAL_DISCLAIMER,
} from "@/data/maintenancePlans";
import { MAINTENANCE_FAQS } from "@/data/faqs";

const BREADCRUMBS = [{ label: "Home", to: "/" }, { label: "Maintenance Plans" }];

function scrollToId(id) {
  if (typeof document === "undefined") return;
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

/**
 * Maintenance Plans Page
 *
 * Designed to be transparent and homeowner-friendly. The page is composed
 * from a set of modular section components in `/components/maintenance/`
 * so this page stays a readable orchestrator.
 *
 * Conversion flow:
 *   Hero CTAs → calculator OR inspection structure OR request form
 *   Calculator → result with monthly price, inspection fee, included crew
 *                hours per claim, annual allowance, "what this means"
 *                → "Use This Plan" prefills the request form and scrolls
 *   Plan card  → "Choose <plan>" prefills the request form and scrolls
 *               + "See What's Included" scrolls to membership-includes
 */
export default function MaintenancePlans() {
  // Lifted prefill state shared between the calculator, plan cards and the
  // membership request form. Each prefill push creates a new object so the
  // form's useEffect always runs (re-applies prefill on repeat selections).
  const [prefill, setPrefill] = useState(null);
  const formRef = useRef(null);

  const focusRequestForm = useCallback(() => {
    scrollToId("request-details");
  }, []);

  const handlePlanCardChoose = useCallback((plan) => {
    setPrefill({
      _stamp: Date.now(),
      plan: plan.name,
      monthly_price: plan.pricePrefix
        ? `${plan.pricePrefix} $${plan.price}/${plan.billing}`
        : `$${plan.price}/${plan.billing}`,
      annual_allowance: plan.allowance,
      crew_hours: plan.cap,
    });
    focusRequestForm();
  }, [focusRequestForm]);

  const handleRecommenderApply = useCallback(({ plan, sqft, tier }) => {
    if (!plan || !tier) return;
    const cell = tier.plans?.[plan.id] || {};
    setPrefill({
      _stamp: Date.now(),
      plan: plan.name,
      sqft: sqft,
      monthly_price: cell.priceLabel || `Starting at $${plan.price}/${plan.billing}`,
      inspection_fee: tier.inspectionFeeLabel || "Custom quote required",
      crew_hours: cell.crewHours || plan.cap,
      annual_allowance: plan.allowance,
    });
    focusRequestForm();
  }, [focusRequestForm]);

  return (
    <>
      <SEO
        title="Maintenance Plans Austin TX | Black Ridge Construction"
        description="Black Ridge Construction offers structured home maintenance memberships, roof check plans, baseline inspections, visit reports, and property care programs for Austin-area homeowners, investors, and property managers."
        jsonLd={buildFaqJsonLd(MAINTENANCE_FAQS)}
      />

      {/* ────────────────────────── Hero ────────────────────────── */}
      <section className="bg-br-black text-white">
        <div className="container-br py-12 lg:py-20">
          <BreadcrumbNav dark items={BREADCRUMBS} />
          <div className="max-w-3xl mt-5">
            <div className="br-eyebrow text-br-bronze-2">Maintenance Memberships</div>
            <h1 className="br-headline text-4xl sm:text-5xl mt-3">
              Home Maintenance Plans Built Around Real Inspections, Reports &amp; Priority Support
            </h1>
            <p className="mt-4 text-gray-300 leading-relaxed">
              Black Ridge Construction maintenance memberships give Austin-area homeowners a structured way to stay ahead of small issues, document visible property conditions, and keep a trusted general contractor familiar with the home before something becomes urgent.
            </p>
            <p className="mt-3 text-gray-300 leading-relaxed">
              Each plan includes scheduled visits, a baseline home profile, inspection-style checklists, documented findings, defined crew-hour limits, and clear next steps when repairs or add-ons are recommended.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => scrollToId("waitlist")}
                className="br-btn-primary"
                data-testid="hero-join-waitlist"
              >
                <Sparkles size={14} /> Join July Waitlist
              </button>
              <button
                type="button"
                onClick={() => scrollToId("calculator")}
                className="br-btn-secondary"
                data-testid="hero-calculate"
              >
                <Calculator size={14} /> Calculate My Plan
              </button>
              <button
                type="button"
                onClick={() => scrollToId("inspection-structure")}
                className="br-btn-secondary"
                data-testid="hero-see-inspect"
              >
                <ClipboardCheck size={14} /> See What We Inspect
              </button>
              <button
                type="button"
                onClick={() => scrollToId("request-details")}
                className="br-btn-secondary"
                data-testid="hero-request-details"
              >
                <FileText size={14} /> Request Membership Details
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── Phase F.1 — July 2026 Waitlist Card (above the fold) ───────── */}
      <section className="bg-br-paper">
        <div className="container-br pt-10 lg:pt-14 -mb-6">
          <MaintenanceWaitlistCard />
        </div>
      </section>

      {/* ────────────────── Quick Calculator / Plan Recommender ────────────────── */}
      <section className="section-pad bg-br-paper">
        <div className="container-br">
          <PlanRecommender onApply={handleRecommenderApply} />
        </div>
      </section>

      {/* ────────────────────────── Residential Plan Cards ────────────────────────── */}
      <section className="section-pad bg-white pt-0 lg:pt-0" id="plans">
        <div className="container-br">
          <div className="max-w-2xl mb-8">
            <div className="br-eyebrow">Residential</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3">Residential Maintenance Memberships</h2>
            <p className="mt-3 text-gray-700 leading-relaxed">
              For homeowners, families, repeat clients, and investors with single-property needs. Each plan starts with a baseline inspection and a documented home profile.
            </p>
            <p className="mt-2 text-xs text-gray-500 italic leading-relaxed">{RESIDENTIAL_PRICING_NOTE}</p>
          </div>
          <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-5">
            {RESIDENTIAL_PLANS.map((p) => (
              <MaintenancePlanCard key={p.id} plan={p} onChoose={handlePlanCardChoose} />
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────── What Your Membership Actually Includes ────────────────── */}
      <MembershipIncludesCards />

      {/* ────────────────────────── Pricing by Home Size ────────────────────────── */}
      <section className="section-pad bg-br-paper" id="pricing-table">
        <div className="container-br">
          <SqftPricingTable />
        </div>
      </section>

      {/* ─────────── Baseline Inspection + Findings Grades + Visit Checklist + Visit Report ─────────── */}
      <MaintenanceInspectionSection />

      {/* ────────────── Crew Hours + Allowance + Add-Ons + Contractor Service ────────────── */}
      <CrewHoursAndAllowanceSection />

      {/* ────────────────────────── Emergency Dispatch & Storm ────────────────────────── */}
      <EmergencyDispatchSection />

      {/* ─────────────── What Is Not Included + Client-Supplied Materials ─────────────── */}
      <ExclusionsAndMaterialsSection />

      {/* ────────────────────────── Commercial / Portfolio Plans ────────────────────────── */}
      <section className="section-pad bg-white" id="commercial">
        <div className="container-br">
          <div className="max-w-2xl mb-8">
            <div className="br-eyebrow">Commercial / Property</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3">Commercial &amp; Portfolio Memberships</h2>
            <p className="mt-3 text-gray-700 leading-relaxed">
              For property managers, investors, multifamily owners, retail sites, and light commercial properties needing recurring maintenance attention, photo documentation, and faster repair coordination.
            </p>
            <p className="mt-2 text-xs text-gray-500 italic leading-relaxed">
              Commercial, rental, and portfolio properties may require custom pricing based on square footage, site type, access, tenant use, frequency, and condition.
            </p>
          </div>
          <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-5">
            {COMMERCIAL_PLANS.map((p) => (
              <MaintenancePlanCard key={p.id} plan={p} onChoose={handlePlanCardChoose} />
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────────── How the Membership Process Works ────────────────────────── */}
      <MembershipProcessSteps />

      {/* ────────────────────────── FAQs ────────────────────────── */}
      <section className="section-pad bg-br-paper" id="faq">
        <div className="container-br grid lg:grid-cols-[1fr_1.4fr] gap-10">
          <div>
            <div className="br-eyebrow">Maintenance Plan FAQs</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3">Common Questions About Memberships</h2>
            <p className="mt-3 text-gray-700 leading-relaxed">
              Memberships are designed to be honest about what they include and exclude. We follow that same principle on this page.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => scrollToId("request-details")}
                className="br-btn-dark"
                data-testid="faq-request-cta"
              >
                Request Membership Details <ArrowRight size={14} />
              </button>
              <Link to="/contact" className="text-[11px] uppercase tracking-[0.25em] text-br-bronze hover:text-br-bronze-2 underline underline-offset-4 self-center">
                Or contact us directly
              </Link>
            </div>
          </div>
          <FAQAccordion faqs={MAINTENANCE_FAQS} />
        </div>
      </section>

      {/* ────────────────────────── Membership Request Form ────────────────────────── */}
      <section className="section-pad bg-white">
        <div className="container-br">
          <MembershipRequestForm ref={formRef} prefill={prefill} />
          <p className="mt-6 text-xs text-gray-500 leading-relaxed max-w-4xl">
            {FINAL_DISCLAIMER}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-gray-500">Already a member?</span>
            <Link to="/members/login" className="text-br-bronze hover:text-br-bronze-2 underline underline-offset-4 font-medium" data-testid="member-portal-link">
              Access your member portal →
            </Link>
          </div>
        </div>
      </section>

      {/* ────────────────────────── Final CTA ────────────────────────── */}
      <MaintenanceFinalCTA onCalculate={() => scrollToId("calculator")} />
    </>
  );
}
